import { useState, useEffect, useCallback } from 'react';
import { X, Search, Sparkles, Trash2, Ban, PiggyBank, Check, Loader2, RotateCcw } from 'lucide-react';
import { paymentReconApi } from '../../services/paymentReconApi';
import { useToast } from '../ui/Toast';
import { ConfirmModal } from '../ui/ConfirmModal';
import { formatMoney, formatDate } from '../../utils/paymentReconFormat';
import { useTableSort } from '../../hooks/useTableSort';
import { SortableTh } from './SortableTh';
import { CommentBadge } from './CommentBadge';
import type { CommentTarget } from './InvoiceCommentsPanel';
import type { ReconTransaction, ReconInvoice, ReconCategory, ReconAllocation, AllocationInput } from '../../types/paymentRecon';

interface Row {
    invoice: ReconInvoice;
    amount: number;                 // cash applied to this invoice
    residualReasonId: number | null; // close the remaining gap as discount/fee/etc.
}

interface Props {
    transaction: ReconTransaction;
    nonCustomerCategories: ReconCategory[];
    residualReasons: ReconCategory[];
    onClose: () => void;
    onSaved: () => void;
    /** Open the per-invoice comments side-panel (stacks above this drawer). */
    onOpenComments: (target: CommentTarget) => void;
}

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

/**
 * Words that carry no customer information in a bank payment text: the operation-type
 * prefix every French statement line starts with ("VIR", "VIR INST", "REM CHQ", …), our
 * own entity names, and generic document vocabulary. Seeding the invoice search with the
 * FIRST token of the counterparty meant every dfr payment searched for "VIR" — which
 * matches "CTP ENVI(VIR)ONNEMENT" and nothing the payer actually is.
 */
const SEED_NOISE = new Set([
    'VIR', 'VIRT', 'VIREMENT', 'INST', 'SEPA', 'SCT', 'PRLV', 'PRELEVEMENT', 'CHQ', 'CHEQUE',
    'REM', 'REMISE', 'RECU', 'RECEPT', 'TIP', 'CB',
    'DOLDER', 'ENECO', 'DISTRIBUTION',
    'FACTURE', 'FACTURES', 'FACT', 'FACTURA', 'INVOICE', 'INV', 'REF', 'REFERENCE', 'REFERENCES',
    'NON', 'TRANSMISE', 'RECHNUNG', 'ZAHLUNG', 'KREDITOR', 'COMPLEMENT', 'REL', 'SOLDE',
    'PAIEMENT', 'PAYMENT', 'NOTRE', 'VOTRE', 'YOUR', 'POUR', 'DES', 'DIE', 'DER',
    'SAS', 'SASU', 'SARL', 'GMBH', 'LTD', 'SPA',
]);

/**
 * Turn a bank payment text into search tokens: the payer name parts and any invoice-like
 * reference. Splitting on non-alphanumerics also breaks "SP-FR2600163-30/04/2026" into
 * "FR2600163", which still matches invoice no "SP-FR2600163" as a substring.
 */
export function seedSearchTerms(counterparty: string | null, reference: string | null): string[] {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const tok of `${counterparty ?? ''} ${reference ?? ''}`.split(/[^A-Za-z0-9]+/)) {
        if (tok.length < 3) continue;
        if (!/[A-Za-z]/.test(tok)) continue;          // pure account / reference numbers
        const up = tok.toUpperCase();
        if (SEED_NOISE.has(up) || seen.has(up)) continue;
        seen.add(up);
        out.push(tok);
        if (out.length >= 6) break;
    }
    return out;
}

export function MatchPanel({ transaction, nonCustomerCategories, residualReasons, onClose, onSaved, onOpenComments }: Props) {
    const { toast } = useToast();
    const [rows, setRows] = useState<Row[]>([]);
    const [searchResults, setSearchResults] = useState<ReconInvoice[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [searching, setSearching] = useState(false);
    const [aiBusy, setAiBusy] = useState(false);
    const [aiReason, setAiReason] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [learnAlias, setLearnAlias] = useState(true);
    const [nonCustomerCat, setNonCustomerCat] = useState<number | ''>('');
    const [nonCustomerNote, setNonCustomerNote] = useState('');
    const [confirmUnmatch, setConfirmUnmatch] = useState(false);

    const txAmount = round2(transaction.Amount);
    const cashApplied = round2(rows.reduce((s, r) => s + r.amount, 0));
    const remaining = round2(txAmount - cashApplied);
    const selectedIds = new Set(rows.map(r => r.invoice.Id));
    // The payer→customer "remember" option only makes sense while assigning a
    // not-yet-resolved payment. On an already-matched transaction it is misleading
    // (auto-match never learns) and would memorise junk, so it's hidden there.
    const canLearn = transaction.Status === 'Unmatched';

    // Search results: hide already-allocated invoices, then sort client-side
    // by the clicked column header.
    const visibleResults = searchResults.filter(i => !selectedIds.has(i.Id));
    const { sorted: sortedResults, sort: resultSort, toggleSort: toggleResultSort } = useTableSort(visibleResults, {
        invoice: (i) => i.InvoiceNo,
        customer: (i) => i.CustomerName,
        date: (i) => i.InvoiceDate,
        open: (i) => i.OpenAmount,
    });

    // Every term is OR-ed (qAny), so a multi-word query still finds an invoice that
    // matches only one of the words — a plain substring search never would.
    const runSearch = useCallback(async (q: string) => {
        setSearching(true);
        try {
            const res = await paymentReconApi.listInvoices({ companyCode: transaction.CompanyCode, status: 'open', qAny: q, limit: 50 });
            setSearchResults(res.invoices);
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Search failed', 'error');
        } finally {
            setSearching(false);
        }
    }, [toast, transaction.CompanyCode]);

    // Initial candidate load: by resolved customer code, else from the payment text.
    // The seed is written into the visible search box — previously the box stayed empty
    // while the list was already filtered, so the results looked like "everything open".
    useEffect(() => {
        const seed = seedSearchTerms(transaction.CounterpartyRaw, transaction.ReferenceRaw).join(' ');
        if (transaction.ResolvedCustomerCode) {
            void paymentReconApi.listInvoices({ companyCode: transaction.CompanyCode, status: 'open', customerCode: transaction.ResolvedCustomerCode, limit: 50 })
                .then(r => {
                    if (r.invoices.length > 0) setSearchResults(r.invoices);
                    else if (seed) setSearchQuery(seed);   // customer has nothing open → fall back to the text
                })
                .catch(() => undefined);
        } else if (seed) {
            setSearchQuery(seed);                          // the debounce effect runs the search
        }
        // Pre-fill from existing allocations (editing an already-matched payment).
        // Group by invoice: cash lines (InvoicePayment / CreditMemoOffset) make up the
        // applied amount; a Residual line is the "close the gap" reason for that invoice,
        // NOT a separate cash row. (Mapping each allocation 1:1 double-counted the residual
        // as cash and dropped the reason.)
        if (transaction.allocations.length > 0) {
            const byInvoice = new Map<number, { meta: ReconAllocation; cash: number; residualReasonId: number | null; addBack: number }>();
            for (const a of transaction.allocations) {
                let g = byInvoice.get(a.InvoiceId);
                if (!g) { g = { meta: a, cash: 0, residualReasonId: null, addBack: 0 }; byInvoice.set(a.InvoiceId, g); }
                g.addBack += a.Amount;                                   // every alloc reduced the invoice's open → add all back
                if (a.AllocationType === 'Residual') g.residualReasonId = a.ResidualReasonId;
                else g.cash += a.Amount;
            }
            setRows([...byInvoice.values()].map(g => ({
                invoice: {
                    Id: g.meta.InvoiceId, InvoiceNo: g.meta.InvoiceNo, CustomerName: g.meta.CustomerName,
                    GrossAmount: g.meta.GrossAmount, OpenAmount: round2(g.meta.OpenAmount + g.addBack), // true open before this payment
                    CommentCount: g.meta.CommentCount ?? 0,
                } as ReconInvoice,
                amount: round2(g.cash),
                residualReasonId: g.residualReasonId,
            })));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Search as you type. It used to fire only on Enter, so a typed customer name looked
    // like "no such invoice" while the list still showed the seeded candidates.
    useEffect(() => {
        const q = searchQuery.trim();
        if (!q) return;                       // empty box → keep the seeded candidate list
        const t = setTimeout(() => { void runSearch(q); }, 300);
        return () => clearTimeout(t);
    }, [searchQuery, runSearch]);

    const addInvoice = (inv: ReconInvoice) => {
        if (selectedIds.has(inv.Id)) return;
        const defaultAmount = inv.GrossAmount < 0
            ? round2(inv.OpenAmount)                       // credit memo: negative
            : round2(Math.min(inv.OpenAmount, Math.max(remaining, 0) || inv.OpenAmount));
        setRows(prev => [...prev, { invoice: inv, amount: defaultAmount, residualReasonId: null }]);
    };

    const updateRow = (id: number, patch: Partial<Row>) => {
        setRows(prev => prev.map(r => r.invoice.Id === id ? { ...r, ...patch } : r));
    };
    const removeRow = (id: number) => setRows(prev => prev.filter(r => r.invoice.Id !== id));

    const buildAllocations = (): AllocationInput[] => {
        const out: AllocationInput[] = [];
        for (const r of rows) {
            const isCredit = r.invoice.GrossAmount < 0;
            out.push({
                invoiceId: r.invoice.Id,
                amount: round2(r.amount),
                allocationType: isCredit ? 'CreditMemoOffset' : 'InvoicePayment',
            });
            if (r.residualReasonId) {
                const gap = round2(r.invoice.OpenAmount - r.amount);
                if (Math.abs(gap) >= 0.01) {
                    out.push({
                        invoiceId: r.invoice.Id,
                        amount: gap,
                        allocationType: 'Residual',
                        residualReasonId: r.residualReasonId,
                    });
                }
            }
        }
        return out;
    };

    const runAi = async () => {
        setAiBusy(true);
        setAiReason(null);
        try {
            const res = await paymentReconApi.aiSuggest(transaction.Id);
            if (!res.suggestion || res.suggestion.invoiceIds.length === 0) {
                toast('No confident suggestion found', 'info');
                setAiReason(res.suggestion?.reasoning || 'No match found.');
                return;
            }
            // Resolve invoice ids → load them and prefill
            const all = await paymentReconApi.listInvoices({ companyCode: transaction.CompanyCode, status: 'open', limit: 2000 });
            const byId = new Map(all.invoices.map(i => [i.Id, i]));
            const next: Row[] = [];
            for (const id of res.suggestion.invoiceIds) {
                const inv = byId.get(id);
                if (inv) next.push({ invoice: inv, amount: round2(inv.OpenAmount), residualReasonId: null });
            }
            if (next.length) setRows(next);
            setAiReason(res.suggestion.reasoning || `${res.source} suggestion (${Math.round((res.suggestion.confidence || 0) * 100)}%)`);
        } catch (e) {
            toast(e instanceof Error ? e.message : 'AI suggest failed', 'error');
        } finally {
            setAiBusy(false);
        }
    };

    const save = async (onAccountRemainder: boolean) => {
        const allocations = buildAllocations();
        if (allocations.length === 0) {
            toast('Add at least one invoice, or use On account / Non-customer', 'info');
            return;
        }
        if (cashApplied - txAmount > 0.005) {
            toast('Allocated cash exceeds the payment amount', 'error');
            return;
        }
        setSaving(true);
        try {
            await paymentReconApi.match({
                transactionId: transaction.Id,
                allocations,
                onAccountRemainder,
                learnAlias: canLearn && learnAlias,
                customerCode: rows[0]?.invoice.CustomerCode ?? transaction.ResolvedCustomerCode,
                customerName: rows[0]?.invoice.CustomerName ?? null,
            });
            toast('Payment matched', 'success');
            onSaved();
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Match failed', 'error');
        } finally {
            setSaving(false);
        }
    };

    const saveNonCustomer = async () => {
        if (!nonCustomerCat) { toast('Choose a category', 'info'); return; }
        setSaving(true);
        try {
            await paymentReconApi.setNonCustomer(transaction.Id, Number(nonCustomerCat), nonCustomerNote.trim() || undefined);
            toast('Marked as non-customer payment', 'success');
            onSaved();
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Failed', 'error');
        } finally {
            setSaving(false);
        }
    };

    const saveOnAccount = async () => {
        setSaving(true);
        try {
            await paymentReconApi.setOnAccount(transaction.Id);
            toast('Parked on account', 'success');
            onSaved();
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Failed', 'error');
        } finally {
            setSaving(false);
        }
    };

    const doUnmatch = async () => {
        setConfirmUnmatch(false);
        setSaving(true);
        try {
            await paymentReconApi.unmatch(transaction.Id);
            toast('Unmatched — back in the review queue', 'success');
            onSaved();
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Failed', 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-40 flex justify-end bg-black/30" onClick={onClose}>
            <div
                className="flex h-full w-full max-w-2xl flex-col bg-white shadow-xl"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-start justify-between border-b border-slate-200 p-4">
                    <div>
                        <div className="text-sm text-slate-500">{formatDate(transaction.BookingDate)} · {transaction.Currency}</div>
                        <div className="text-xl font-semibold text-slate-800">{formatMoney(transaction.Amount, transaction.Currency)}</div>
                        <div className="mt-0.5 text-sm text-slate-600">{transaction.CounterpartyRaw || '(no counterparty)'}</div>
                        {transaction.ReferenceRaw && (
                            <div className="mt-0.5 max-w-md truncate text-xs text-slate-400" title={transaction.ReferenceRaw}>
                                Ref: {transaction.ReferenceRaw}
                            </div>
                        )}
                    </div>
                    <button onClick={onClose} className="rounded p-1 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
                </div>

                {/* Allocations builder */}
                <div className="flex-1 overflow-y-auto p-4">
                    <div className="mb-3 flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-slate-700">Allocated invoices</h3>
                        <button
                            onClick={runAi}
                            disabled={aiBusy}
                            className="inline-flex items-center gap-1 rounded-md border border-violet-200 bg-violet-50 px-2 py-1 text-xs font-medium text-violet-700 hover:bg-violet-100 disabled:opacity-50"
                        >
                            {aiBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                            AI suggest
                        </button>
                    </div>
                    {aiReason && <div className="mb-3 rounded bg-violet-50 p-2 text-xs text-violet-700">{aiReason}</div>}

                    {rows.length === 0 && (
                        <div className="mb-3 rounded border border-dashed border-slate-200 p-4 text-center text-sm text-slate-400">
                            No invoices selected yet. Search below or use AI suggest.
                        </div>
                    )}

                    {rows.map((r) => {
                        const gap = round2(r.invoice.OpenAmount - r.amount);
                        return (
                            <div key={r.invoice.Id} className="mb-2 rounded-lg border border-slate-200 p-2">
                                <div className="flex items-center justify-between">
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-1.5 truncate text-sm font-medium text-slate-700">
                                            {r.invoice.InvoiceNo}
                                            <CommentBadge
                                                count={r.invoice.CommentCount ?? 0}
                                                onClick={() => onOpenComments({ invoiceId: r.invoice.Id, invoiceNo: r.invoice.InvoiceNo, customerName: r.invoice.CustomerName })}
                                            />
                                        </div>
                                        <div className="truncate text-xs text-slate-500">{r.invoice.CustomerName} · open {formatMoney(r.invoice.OpenAmount)}</div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={r.amount}
                                            onChange={(e) => updateRow(r.invoice.Id, { amount: Number(e.target.value) })}
                                            className="w-28 rounded border border-slate-300 px-2 py-1 text-right text-sm"
                                        />
                                        <button onClick={() => removeRow(r.invoice.Id)} className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600">
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>
                                {Math.abs(gap) >= 0.01 && r.invoice.GrossAmount >= 0 && (
                                    <div className="mt-1 flex items-center gap-2 text-xs text-slate-500">
                                        <span>Gap {formatMoney(gap)} →</span>
                                        <select
                                            value={r.residualReasonId ?? ''}
                                            onChange={(e) => updateRow(r.invoice.Id, { residualReasonId: e.target.value ? Number(e.target.value) : null })}
                                            className="rounded border border-slate-300 px-1 py-0.5 text-xs"
                                        >
                                            <option value="">leave open</option>
                                            {residualReasons.map(rr => <option key={rr.Id} value={rr.Id}>close as {rr.Label}</option>)}
                                        </select>
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    {/* Search */}
                    <div className="mt-4">
                        <div className="relative">
                            <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
                            <input
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') void runSearch(searchQuery); }}
                                placeholder="Search open invoices (number or customer)…"
                                className="w-full rounded-md border border-slate-300 py-2 pl-8 pr-8 text-sm"
                            />
                            {searchQuery && (
                                <button
                                    onClick={() => setSearchQuery('')}
                                    title="Clear search"
                                    className="absolute right-2 top-2.5 rounded text-slate-400 hover:text-slate-600"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            )}
                        </div>
                        <div className="mt-1 text-xs text-slate-400">
                            {searchQuery
                                ? 'Any word may match — invoice number or customer name.'
                                : transaction.ResolvedCustomerCode
                                    ? 'Showing all open invoices of the linked customer.'
                                    : 'Type to search all open invoices.'}
                        </div>
                        <div className="mt-2 max-h-48 overflow-y-auto rounded-md border border-slate-100">
                            {searching && <div className="p-3 text-center text-xs text-slate-400">Searching…</div>}
                            {!searching && sortedResults.length > 0 && (
                                <table className="w-full text-left">
                                    <thead className="sticky top-0 z-10 bg-slate-50 text-xs font-medium text-slate-500">
                                        <tr>
                                            <SortableTh label="Invoice" sortKey="invoice" sort={resultSort} onSort={toggleResultSort} />
                                            <SortableTh label="Customer" sortKey="customer" sort={resultSort} onSort={toggleResultSort} />
                                            <SortableTh label="Date" sortKey="date" sort={resultSort} onSort={toggleResultSort} />
                                            <SortableTh label="Open" sortKey="open" sort={resultSort} onSort={toggleResultSort} className="text-right" />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedResults.map(inv => (
                                            <tr
                                                key={inv.Id}
                                                onClick={() => addInvoice(inv)}
                                                className="cursor-pointer border-t border-slate-50 hover:bg-amber-50"
                                            >
                                                <td className="whitespace-nowrap px-3 py-1.5 text-sm text-slate-700">
                                                    <span className="inline-flex items-center gap-1.5">
                                                        {inv.InvoiceNo}
                                                        <CommentBadge
                                                            count={inv.CommentCount ?? 0}
                                                            onClick={() => onOpenComments({ invoiceId: inv.Id, invoiceNo: inv.InvoiceNo, customerName: inv.CustomerName })}
                                                        />
                                                    </span>
                                                </td>
                                                <td className="max-w-[12rem] truncate px-3 py-1.5 text-xs text-slate-500" title={inv.CustomerName ?? ''}>{inv.CustomerName}</td>
                                                <td className="whitespace-nowrap px-3 py-1.5 text-xs text-slate-500">{formatDate(inv.InvoiceDate)}</td>
                                                <td className={`whitespace-nowrap px-3 py-1.5 text-right text-sm ${inv.GrossAmount < 0 ? 'text-emerald-700' : 'text-slate-700'}`}>{formatMoney(inv.OpenAmount)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                            {!searching && sortedResults.length === 0 && (
                                <div className="p-3 text-center text-xs text-slate-400">
                                    {searchQuery ? 'No open invoice matches this search.' : 'Type to search open invoices.'}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer / totals + actions */}
                <div className="border-t border-slate-200 p-4">
                    <div className="mb-3 flex items-center justify-between text-sm">
                        <span className="text-slate-500">Cash applied <span className="font-medium text-slate-700">{formatMoney(cashApplied)}</span></span>
                        <span className={remaining > 0.005 ? 'text-amber-700' : remaining < -0.005 ? 'text-rose-700' : 'text-emerald-700'}>
                            Remaining {formatMoney(remaining)}
                        </span>
                    </div>

                    {canLearn && (
                        <label className="mb-3 flex items-center gap-2 text-xs text-slate-600">
                            <input type="checkbox" checked={learnAlias} onChange={(e) => setLearnAlias(e.target.checked)} />
                            Remember “{transaction.CounterpartyRaw || 'this payer'}” → customer for future auto-matching
                        </label>
                    )}

                    <div className="flex flex-wrap gap-2">
                        <button
                            onClick={() => save(false)}
                            disabled={saving || rows.length === 0}
                            className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                        >
                            <Check className="h-4 w-4" /> Save match
                        </button>
                        {remaining > 0.005 && rows.length > 0 && (
                            <button
                                onClick={() => save(true)}
                                disabled={saving}
                                className="inline-flex items-center gap-1 rounded-md bg-sky-600 px-3 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-50"
                            >
                                <PiggyBank className="h-4 w-4" /> Save + park remainder
                            </button>
                        )}
                        <button
                            onClick={saveOnAccount}
                            disabled={saving}
                            className="inline-flex items-center gap-1 rounded-md border border-sky-300 bg-white px-3 py-2 text-sm font-medium text-sky-700 hover:bg-sky-50 disabled:opacity-50"
                        >
                            <PiggyBank className="h-4 w-4" /> On account
                        </button>
                    </div>

                    {transaction.Status !== 'Unmatched' && (
                        <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3">
                            <span className="text-xs text-slate-400">Currently {transaction.Status}.</span>
                            <button
                                onClick={() => setConfirmUnmatch(true)}
                                disabled={saving}
                                className="inline-flex items-center gap-1 rounded-md border border-rose-200 bg-white px-3 py-1.5 text-sm font-medium text-rose-600 hover:bg-rose-50 disabled:opacity-50"
                            >
                                <RotateCcw className="h-4 w-4" /> Unmatch
                            </button>
                        </div>
                    )}

                    <div className="mt-3 border-t border-slate-100 pt-3">
                        <div className="flex items-center gap-2">
                            <Ban className="h-4 w-4 text-violet-600" />
                            <select
                                value={nonCustomerCat}
                                onChange={(e) => setNonCustomerCat(e.target.value ? Number(e.target.value) : '')}
                                className="rounded border border-slate-300 px-2 py-1.5 text-sm"
                            >
                                <option value="">Not a customer payment…</option>
                                {nonCustomerCategories.map(c => <option key={c.Id} value={c.Id}>{c.Label}</option>)}
                            </select>
                            <button
                                onClick={saveNonCustomer}
                                disabled={saving || !nonCustomerCat}
                                className="rounded-md border border-violet-300 bg-white px-3 py-1.5 text-sm font-medium text-violet-700 hover:bg-violet-50 disabled:opacity-50"
                            >
                                Mark
                            </button>
                        </div>
                        {nonCustomerCat !== '' && (
                            <input
                                value={nonCustomerNote}
                                onChange={(e) => setNonCustomerNote(e.target.value)}
                                placeholder="Note (optional — recommended for “Other”)"
                                className="mt-2 w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
                            />
                        )}
                    </div>
                </div>

                {confirmUnmatch && (
                    <ConfirmModal
                        title="Unmatch payment"
                        message={`Remove all allocations and send this ${formatMoney(transaction.Amount, transaction.Currency)} payment back to the review queue as Unmatched? Any on-account / non-customer marking is cleared. This is auditable and reversible.`}
                        confirmLabel="Unmatch"
                        variant="danger"
                        onConfirm={doUnmatch}
                        onCancel={() => setConfirmUnmatch(false)}
                    />
                )}
            </div>
        </div>
    );
}
